import { Box, Flex } from '@mantine/core';
import { useState, useMemo, useEffect } from 'react';

import { NewDialogsChart } from '../components/NewDialogs/Chart';
import { Header } from '../components/NewDialogs/Header';
import { PagesFilter } from '../components/NewDialogs/PagesFilter';
import { StatusDisplay } from '../components/NewDialogs/StatusDisplay';
import { NewDialogsTable } from '../components/NewDialogs/Table';
import { useDialogsHistory } from '../hooks/useDialogsHistory';
import { useNewDialogsData } from '../hooks/useNewDialogsData';

import { useGetPages } from '@/features/pages';
import { Platforms } from '@/types';

export const NewDialogs = () => {
  const { historyRef, updateTimesRef, pushStats } = useDialogsHistory();
  const { stats, sortedStats, pageKeys, updateStats, error, isLoading } =
    useNewDialogsData(pushStats);
  const tgPages = useGetPages({ platform: Platforms.telegram });
  const instPages = useGetPages({ platform: Platforms.instagram });

  const pagesMap = useMemo(() => {
    const map: Record<string, { name: string }> = {};

    [...(tgPages.data?.pages ?? []), ...(instPages.data?.pages ?? [])].forEach((p) => {
      map[p._id] = { name: p.name };
    });

    return map;
  }, [tgPages.data, instPages.data]);

  const [activePages, setActivePages] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (pageKeys.length > 0) {
      setActivePages(new Set(pageKeys));
    }
  }, [pageKeys]);

  const togglePage = (page: string) =>
    setActivePages((prev) => {
      const next = new Set(prev);
      next.has(page) ? next.delete(page) : next.add(page);
      return next;
    });

  const pageColors = useMemo(() => {
    return pageKeys.reduce<Record<string, { line: string; fill: string }>>((acc, page, idx) => {
      const hue = Math.round((360 * idx) / pageKeys.length);
      acc[page] = { line: `hsl(${hue},70%,50%)`, fill: `hsla(${hue},70%,50%,0.2)` };
      return acc;
    }, {});
  }, [pageKeys]);

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header updateStats={updateStats} updatedAt={stats?.respondedAt} />

      <StatusDisplay error={error} isEmptyData={!isLoading && pageKeys.length === 0} />

      {!error && pageKeys.length > 0 && (
        <Flex p={20} gap={20} style={{ flex: 1, alignItems: 'flex-start' }}>
          <Box style={{ flex: 3, overflowY: 'auto' }}>
            <NewDialogsTable stats={sortedStats} pageColors={pageColors} pagesMap={pagesMap} />
          </Box>

          <Box p={2} style={{ flex: 7, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <PagesFilter
              activePages={activePages}
              pageIds={pageKeys}
              togglePage={togglePage}
              onSelectAll={() => setActivePages(new Set(pageKeys))}
              onReset={() => setActivePages(new Set())}
            />

            <Box
              style={{ flex: 1, padding: '25px 15px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.57)' }}
            >
              <NewDialogsChart
                activePages={activePages}
                pageColors={pageColors}
                pageIds={pageKeys}
                historyRef={historyRef}
                updateTimesRef={updateTimesRef}
              />
            </Box>
          </Box>
        </Flex>
      )}
    </Box>
  );
};
